import { apiRequest } from "@/lib/api";

export type SubscriptionPlanStatRow = {
  squarePlanId: string;
  enrolledCount: number;
  displayName: string | null;
  amount: number | null;
  planType: string | null;
  cadence: string | null;
  numberOfPayments: number | null;
  status: string | null;
  inCatalog: boolean;
};

export type PlanEnrollmentStudentRow = {
  orderId: string;
  enrolledAt: string;
  program: string;
  programName: string | null;
  deliveryMode: string;
  orderStatus: string;
  paymentAmount: number | null;
  student: {
    _id: string;
    accountId: string | null;
    email: string;
    fullName: string | null;
    cellPhoneNumber: string | null;
  };
};

export type PlanEnrollmentListResponse = {
  success: boolean;
  message: string;
  data: {
    enrollments: PlanEnrollmentStudentRow[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
      pages: number;
    };
  };
};

export type SubscriptionPlanStatsResponse = {
  success: boolean;
  message: string;
  data: {
    stats: SubscriptionPlanStatRow[];
  };
};

export async function fetchSubscriptionPlanStats(): Promise<SubscriptionPlanStatRow[]> {
  const res = await apiRequest<SubscriptionPlanStatsResponse>(
    "/api/admin/subscription-plans/stats",
    { method: "GET" }
  );
  return res.data?.stats ?? [];
}

export type AdminSubscriptionPlanListRow = {
  displayName: string;
  paymentOptionName: string | null;
  programTitle: string | null;
  programDeliveryMode: string | null;
  squarePlanId: string | null;
  slug?: string;
  amount: number;
  cadence: string | null;
  numberOfPayments: number;
  status: string;
  planType: string;
  isOneTime: boolean;
  active: boolean;
  enrolledCount: number;
  inCatalog: boolean;
};

export type SubscriptionPlanListQuery = {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string | null;
  planType?: string | null;
  hideInactive?: boolean;
  hideNoSquareId?: boolean;
  all?: boolean;
};

export type SubscriptionPlanListResponse = {
  success: boolean;
  message: string;
  data: {
    plans: AdminSubscriptionPlanListRow[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
      pages: number;
    };
  };
};

export async function fetchSubscriptionPlansList(
  params: SubscriptionPlanListQuery = {}
): Promise<SubscriptionPlanListResponse["data"]> {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set("page", String(params.page));
  if (params.perPage != null) qs.set("perPage", String(params.perPage));
  if (params.search?.trim()) qs.set("search", params.search.trim());
  if (params.status) qs.set("status", params.status);
  if (params.planType) qs.set("planType", params.planType);
  if (params.hideInactive) qs.set("hideInactive", "true");
  if (params.hideNoSquareId) qs.set("hideNoSquareId", "true");
  if (params.all) qs.set("all", "true");

  const query = qs.toString();
  const res = await apiRequest<SubscriptionPlanListResponse>(
    `/api/admin/subscription-plans/list${query ? `?${query}` : ""}`,
    { method: "GET" }
  );

  return (
    res.data ?? {
      plans: [],
      pagination: { page: 1, perPage: params.perPage ?? 25, total: 0, pages: 0 },
    }
  );
}

export async function fetchPlanEnrollments(
  squarePlanId: string,
  page = 1,
  perPage = 25
): Promise<PlanEnrollmentListResponse["data"]> {
  const qs = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
  });
  const res = await apiRequest<PlanEnrollmentListResponse>(
    `/api/admin/subscription-plans/${encodeURIComponent(squarePlanId)}/enrollments?${qs}`,
    { method: "GET" }
  );
  return (
    res.data ?? {
      enrollments: [],
      pagination: { page: 1, perPage, total: 0, pages: 0 },
    }
  );
}
